import { QueryClient } from "@tanstack/react-query";

import {
  formatErrorForLog,
  isAuthError,
  isNetworkError,
  isOAuthTokenUnavailable,
  trackNetworkError,
} from "./errorUtils";
import { isClientSignOutInProgress } from "./signOutGate";

/**
 * Minimal logger interface that both the web `StaticLogger` (`message, ...args`)
 * and the desktop pino child logger (`message, ...args`) satisfy.
 */
export interface QueryLogger {
  error: (message: string, ...args: unknown[]) => void;
  debug: (message: string, ...args: unknown[]) => void;
  /** Optional; when missing, auth issues fall back to `debug`. */
  warn?: (message: string, ...args: unknown[]) => void;
}

const NO_OP_LOGGER: QueryLogger = {
  error: () => {},
  debug: () => {},
};

/**
 * Number of times a transient auth (401) failure is retried before the
 * error is surfaced.
 *
 * A 401 frequently means the session token was mid-refresh when the request
 * was sent (clock skew, token rotation, waking from sleep). Retrying with the
 * shared exponential backoff gives the auth layer time to re-establish a valid
 * session before the next attempt, which recovers automatically in the common
 * case instead of failing the query/mutation outright.
 */
export const AUTH_RETRY_LIMIT = 2;

/** Number of times network errors are retried (e.g. flaky connection, sleep/wake). */
const NETWORK_RETRY_LIMIT = 3;

type RetryDecision = (failureCount: number, error: Error) => boolean | undefined;

/**
 * The branches every retry policy shares: never while signing out, a bounded
 * retry for a transient 401 (the server refused before doing anything, so it
 * is safe even for a write), and the sleep/wake network recovery. `undefined`
 * means "not decided here".
 */
const sharedRetry: RetryDecision = (failureCount, error) => {
  if (isClientSignOutInProgress()) return false;
  if (isAuthError(error)) {
    // OAuth provider token missing (e.g. Spotify/Google disconnected) is not
    // transient — the member must reconnect, so retrying never succeeds.
    if (isOAuthTokenUnavailable(error)) return false;
    // Otherwise treat it as a transient session error: retry (with backoff)
    // to let auth re-establish before giving up.
    return failureCount < AUTH_RETRY_LIMIT;
  }
  if (isNetworkError(error)) return failureCount < NETWORK_RETRY_LIMIT;
  return undefined;
};

function errorStatus(error: Error): number | undefined {
  const status = (error as unknown as { status?: unknown })?.status;
  return typeof status === "number" ? status : undefined;
}

/** Queries are reads: anything but a 404 may be tried again. */
function queryRetry(failureCount: number, error: Error): boolean {
  return (
    sharedRetry(failureCount, error) ??
    (failureCount < NETWORK_RETRY_LIMIT && errorStatus(error) !== 404)
  );
}

/**
 * Mutations are writes, and by default one click sends one write (TX-5127).
 *
 * Only the shared branches retry. Everything else does not:
 * - a 4xx is a refusal, and the same request gets the same answer (one "Add"
 *   used to send a duplicate 409 four times, with the toast 3.5s late);
 * - a 5xx on a non-idempotent write may already have written, so replaying it
 *   can write twice;
 * - an error with NO status cannot be told apart from an HTTP refusal — about
 *   half the web's fetch helpers throw a bare `new Error(message)` after a
 *   non-ok response — so it is treated as one.
 *
 * A write that is safe to repeat opts in with {@link retryServerErrors}.
 */
function mutationRetry(failureCount: number, error: Error): boolean {
  return sharedRetry(failureCount, error) ?? false;
}

/**
 * Opt-in retry for a write that is safe to repeat (the server dedupes it, or it
 * is idempotent): the shared branches, plus up to 3 retries of a 5xx. Never a
 * 4xx, never a status-less error.
 *
 * `useMutation({ mutationFn, retry: retryServerErrors })`
 */
export function retryServerErrors(failureCount: number, error: Error): boolean {
  const shared = sharedRetry(failureCount, error);
  if (shared !== undefined) return shared;
  const status = errorStatus(error);
  return status !== undefined && status >= 500 && failureCount < NETWORK_RETRY_LIMIT;
}

export interface CreateQueryClientOptions {
  /** Logger used for global query/mutation error handlers. Falls back to a no-op logger. */
  logger?: QueryLogger;
}

/**
 * Creates a `QueryClient` with shared defaults used across web and desktop.
 *
 * Includes:
 * - Exponential backoff with jitter for retries
 * - Auth-aware and network-aware retry logic
 * - Global error handlers with sleep/wake detection
 * - Aggressive GC to prevent memory bloat during long sessions
 */
export function createQueryClient(
  options?: CreateQueryClientOptions
): QueryClient {
  const logger = options?.logger ?? NO_OP_LOGGER;

  const retryDelay = (attemptIndex: number): number => {
    const base = Math.min(1000 * 2 ** attemptIndex, 30_000);
    const jitter = base * (0.3 + Math.random() * 0.4);
    return Math.round(jitter);
  };

  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // Wake/resume is coordinated via explicit invalidation + wakeRecoveryGate;
        // global focus/reconnect refetch caused overlapping storms with pollers.
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        retryDelay,
        retry: queryRetry,
        staleTime: 1000 * 60 * 2,
        gcTime: 1000 * 60 * 2,
        structuralSharing: true,
        networkMode: "online",
      },
      mutations: {
        retryDelay,
        retry: mutationRetry,
        gcTime: 1000 * 30,
        networkMode: "online",
      },
    },
  });

  client.getQueryCache().config.onError = (error) => {
    if (isAuthError(error)) {
      if (isOAuthTokenUnavailable(error)) {
        logger.debug(
          `[queryClient] OAuth token not available (reconnect needed): ${formatErrorForLog(error)}`
        );
      } else {
        const msg = `[queryClient] Query auth error: ${formatErrorForLog(error)}`;
        if (typeof logger.warn === "function") {
          logger.warn(msg, error);
        } else {
          logger.debug(msg, error);
        }
      }
    } else if (isNetworkError(error)) {
      const isWakingFromSleep = trackNetworkError();
      if (isWakingFromSleep) {
        logger.debug(
          "[queryClient] Network error (likely waking from sleep):",
          error
        );
      } else {
        logger.debug("[queryClient] Query network error:", error);
      }
    }
  };

  client.getMutationCache().config.onError = (error) => {
    if (isAuthError(error)) {
      if (isOAuthTokenUnavailable(error)) {
        logger.debug(
          `[queryClient] OAuth token not available (reconnect needed): ${formatErrorForLog(error)}`
        );
      } else {
        const msg = `[queryClient] Mutation auth error: ${formatErrorForLog(error)}`;
        if (typeof logger.warn === "function") {
          logger.warn(msg, error);
        } else {
          logger.debug(msg, error);
        }
      }
    } else if (isNetworkError(error)) {
      const isWakingFromSleep = trackNetworkError();
      if (isWakingFromSleep) {
        logger.debug(
          "[queryClient] Network error (likely waking from sleep):",
          error
        );
      } else {
        logger.debug("[queryClient] Mutation network error:", error);
      }
    }
  };

  return client;
}
